import { Individual } from '@aslanpay/portal-common-lib';
import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';

import {
  authenticatedGiftingPaths,
  authenticatedPaths,
  unauthenticatedPaths,
} from 'app/constants/routes';
import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useIsPotManager from 'app/hooks/useIsPotManager';
import { canAccessModule } from 'app/utils/moduleAccess';

import AuthenticatedLayout from 'app/components/Layout/AuthenticatedLayout/AuthenticatedLayout';
import AuthLayout from 'app/components/Layout/AuthLayout';
import LoginLayout from 'app/components/Layout/LoginLayout';
import OnboardingLayout from 'app/components/Layout/OnboardingLayout';
import { employeeProfileLoader } from 'app/pages/ManagePeople/EmployeeProfile/DefaultEmployeeProfile';
import { giftingEmployeeProfileLoader } from 'app/pages/ManagePeople/EmployeeProfile/GiftingEmployeeProfile';
import { adminProfileLoader } from 'app/pages/ManagePeople/AdminProfile/AdminProfile';
import AddBenefit from 'app/pages/Benefits/AddBenefit';
import EditBenefit from 'app/pages/Benefits/EditBenefit';
import PotPage from 'app/pages/Pot/Pot';

type AppPaths = typeof authenticatedPaths & typeof authenticatedGiftingPaths;

// Lazy load pages
const ErrorPage = lazy(() => import('app/pages/Error'));
const Authentication = lazy(() => import('app/pages/Authentication'));
const MyProfile = lazy(() => import('app/pages/MyProfile'));
const ChangePassword = lazy(() => import('app/pages/ChangePassword'));
const Gifting = lazy(() => import('app/pages/Gifting'));
const RewardEmployees = lazy(() => import('app/pages/RewardEmployees'));
const Reports = lazy(() => import('app/pages/Reports'));
const SSOCallback = lazy(() => import('app/pages/SSOCallback'));
const Onboarding = lazy(() => import('app/pages/Onboarding/Onboarding'));
const WalletsAndPots = lazy(() => import('app/pages/WalletsAndPots/WalletsAndPots'));
const DefaultHomepage = lazy(() => import('app/pages/Homepage/DefaultHomepage'));
const GiftingHomepage = lazy(() => import('app/pages/Homepage/GiftingHomepage'));
const EmployeeDashboard = lazy(() => import('app/pages/EmployeeDashboard'));
const DefaultImportEmployees = lazy(
  () => import('app/pages/ImportEmployees/DefaultImportEmployees')
);
const GiftingImportEmployees = lazy(
  () => import('app/pages/ImportEmployees/GiftingImportEmployees')
);

// Footer Pages
const PrivacyPolicy = lazy(() => import('app/pages/Footer/PrivacyPolicy/PrivacyPolicy'));
const Sitemap = lazy(() => import('app/pages/Footer/Sitemap/Sitemap'));
const TermsAndConditions = lazy(
  () => import('app/pages/Footer/TermsAndConditions/TermsAndConditions')
);

// R&R Pages
const RewardAndRecognitionOverview = lazy(
  () => import('app/pages/RewardAndRecognition/Overview/Overview')
);
const MyPrides = lazy(() => import('app/pages/RewardAndRecognition/MyPrides'));
const TheRoar = lazy(() => import('app/pages/RewardAndRecognition/TheRoar'));
const Approvals = lazy(() => import('app/pages/RewardAndRecognition/Approvals'));
const RecognitionAudit = lazy(() => import('app/pages/RewardAndRecognition/RecognitionAudit'));
const CreateRecognition = lazy(() => import('app/pages/RewardAndRecognition/CreateRecognition'));
const Notifications = lazy(() => import('app/pages/Notifications'));

// Management pages
const DefaultEmployeeProfile = lazy(
  () => import('app/pages/ManagePeople/EmployeeProfile/DefaultEmployeeProfile')
);
const GiftingEmployeeProfile = lazy(
  () => import('app/pages/ManagePeople/EmployeeProfile/GiftingEmployeeProfile')
);
const AdminsPage = lazy(() => import('app/pages/ManagePeople/ManageAdmins/AdminsPage'));
const AdminProfile = lazy(() => import('app/pages/ManagePeople/AdminProfile/AdminProfile'));
const GiftingManageAdminsPage = lazy(
  () => import('app/pages/ManagePeople/ManageAdmins/GiftingManageAdminsPage')
);
const DefaultManageEmployeesPage = lazy(
  () => import('app/pages/ManagePeople/ManageEmployees/DefaultManageEmployeesPage')
);
const GiftingManageEmployeesPage = lazy(
  () => import('app/pages/ManagePeople/ManageEmployees/GiftingManageEmployeesPage')
);

// Company Benefits Pages
const BenefitsOverview = lazy(() => import('app/pages/Benefits/Overview/Overview'));
const BenefitsManager = lazy(() => import('app/pages/Benefits/Manager/Manager'));
const BenefitDetails = lazy(() => import('app/pages/Benefits/BenefitDetails'));

const getProfileCrumb = (data: Individual) =>
  data.firstName ? `${data.firstName} ${data.lastName}` : data.email;

function featureGuard(flag: boolean | null, element: React.ReactNode): React.ReactNode {
  if (flag === null) return null;
  if (flag) return element;
  return <Navigate to="/" replace />;
}

function loginContextGuard(isAllowed: boolean, element: React.ReactNode): React.ReactNode {
  return isAllowed ? element : <Navigate to="/" replace />;
}

function getHomepageElement(
  isEmployeeContext: boolean,
  isGifting: boolean,
  isRewardAndRecognitionEnabled: boolean | null,
  isBenefitHostingEnabled: boolean | null
): React.ReactNode {
  if (isEmployeeContext) {
    if (isRewardAndRecognitionEnabled === null || isBenefitHostingEnabled === null) return null;
    if (isRewardAndRecognitionEnabled) {
      return <Navigate to="/reward-and-recognition/the-roar" replace />;
    }
    if (isBenefitHostingEnabled) return <Navigate to="/benefits" replace />;
    return <Navigate to="/employee-dashboard" replace />;
  }

  return isGifting ? <GiftingHomepage /> : <DefaultHomepage />;
}

function renderSharedAuthenticatedRoutes(
  paths: typeof authenticatedPaths | typeof authenticatedGiftingPaths,
  isRewardAndRecognitionEnabled: boolean | null,
  isBenefitHostingEnabled: boolean | null,
  isEmployeeContext: boolean,
  canApproveRecognitions: boolean | null
) {
  return (
    <>
      <Route
        path={paths.profile.path}
        element={<MyProfile />}
        handle={{ crumb: () => paths.profile.crumb }}
      />
      <Route
        path={paths.changePassword.path}
        element={<ChangePassword />}
        handle={{ crumb: () => paths.changePassword.crumb }}
      />
      <Route
        path={paths.rewardEmployees.path}
        element={loginContextGuard(!isEmployeeContext, <RewardEmployees />)}
        handle={{ crumb: () => paths.rewardEmployees.crumb }}
      />
      <Route
        path={paths.privacyPolicy.path}
        element={<PrivacyPolicy />}
        handle={{ crumb: () => paths.privacyPolicy.crumb }}
      />
      <Route
        path={paths.termsAndConditions.path}
        element={<TermsAndConditions />}
        handle={{ crumb: () => paths.termsAndConditions.crumb }}
      />
      <Route
        path={paths.sitemap.path}
        element={<Sitemap />}
        handle={{ crumb: () => paths.sitemap.crumb }}
      />
      <Route
        path={paths.rewardAndRecognitionOverview.path}
        // The employee portal nav is flat, so there is no group to name: its pages
        // breadcrumb as just the page itself.
        handle={isEmployeeContext ? undefined : { crumb: () => 'Reward & Recognition' }}
      >
        <Route
          index
          element={loginContextGuard(
            !isEmployeeContext,
            featureGuard(isRewardAndRecognitionEnabled, <RewardAndRecognitionOverview />)
          )}
          handle={{ crumb: () => paths.rewardAndRecognitionOverview.crumb }}
        />
        <Route
          path={paths.myPrides.path}
          element={loginContextGuard(
            !isEmployeeContext,
            featureGuard(isRewardAndRecognitionEnabled, <MyPrides />)
          )}
          handle={{ crumb: () => paths.myPrides.crumb }}
        />
        <Route
          path={paths.theRoar.path}
          element={featureGuard(isRewardAndRecognitionEnabled, <TheRoar />)}
          handle={{ crumb: () => paths.theRoar.crumb }}
        />
        <Route
          path={paths.createRecognition.path}
          element={loginContextGuard(
            isEmployeeContext,
            featureGuard(isRewardAndRecognitionEnabled, <CreateRecognition />)
          )}
          handle={{ crumb: () => paths.createRecognition.crumb }}
        />
        <Route
          path={paths.approvals.path}
          element={
            canApproveRecognitions === null
              ? null
              : loginContextGuard(
                  canApproveRecognitions,
                  featureGuard(isRewardAndRecognitionEnabled, <Approvals />)
                )
          }
          handle={{ crumb: () => paths.approvals.crumb }}
        />
        <Route
          path={paths.recognitionAudit.path}
          element={loginContextGuard(
            !isEmployeeContext,
            featureGuard(isRewardAndRecognitionEnabled, <RecognitionAudit />)
          )}
          handle={{ crumb: () => paths.recognitionAudit.crumb }}
        />
      </Route>
      <Route path={paths.benefitsOverview.path} handle={{ crumb: () => 'Benefits' }}>
        <Route
          index
          element={featureGuard(isBenefitHostingEnabled, <BenefitsOverview />)}
          // Employees have no Benefits group above this page, so the parent 'Benefits'
          // crumb already names it; an 'Overview' crumb would only repeat the nav item.
          handle={isEmployeeContext ? undefined : { crumb: () => paths.benefitsOverview.crumb }}
        />
        <Route
          path={paths.benefitsManager.path}
          element={loginContextGuard(
            !isEmployeeContext,
            featureGuard(isBenefitHostingEnabled, <BenefitsManager />)
          )}
          handle={{ crumb: () => paths.benefitsManager.crumb }}
        />
        <Route
          path={paths.addBenefit.path}
          element={loginContextGuard(
            !isEmployeeContext,
            featureGuard(isBenefitHostingEnabled, <AddBenefit />)
          )}
          handle={{ crumb: () => paths.addBenefit.crumb }}
        />
        <Route
          path={paths.editBenefit.path}
          element={loginContextGuard(
            !isEmployeeContext,
            featureGuard(isBenefitHostingEnabled, <EditBenefit />)
          )}
          handle={{ crumb: () => paths.editBenefit.crumb }}
        />
        <Route
          path=":id"
          element={featureGuard(isBenefitHostingEnabled, <BenefitDetails />)}
          handle={{ crumb: () => 'Benefit Details' }}
        />
      </Route>
      <Route
        path={paths.notifications.path}
        element={loginContextGuard(isEmployeeContext, <Notifications />)}
        handle={{ crumb: () => paths.notifications.crumb }}
      />
    </>
  );
}

function Routes() {
  const queryClient = useQueryClient();
  const { activeLoginContext, company, user } = useAuthContext();
  const isGifting = company?.type === 'GIFTING_ONLY';
  const isEmployeeContext = activeLoginContext === 'EMPLOYEE';
  const { isPotManager, isLoading: isLoadingPotManager } = useIsPotManager(isEmployeeContext);
  let canApproveRecognitions: boolean | null = true;
  if (isEmployeeContext) {
    canApproveRecognitions = isLoadingPotManager ? null : isPotManager;
  }

  const { data: companyConfig, isLoading: isLoadingConfig } = useFetchCompanyConfiguration(
    company?.id || ''
  );
  const rewardAndRecognitionStatus = isLoadingConfig
    ? null
    : companyConfig?.modulesConfig?.modules?.REWARD_AND_RECOGNITION;
  const benefitHostingStatus = isLoadingConfig
    ? null
    : companyConfig?.modulesConfig?.modules?.BENEFITS_HOSTING;
  const isRewardAndRecognitionEnabled =
    rewardAndRecognitionStatus === null
      ? null
      : canAccessModule(rewardAndRecognitionStatus, user?.role);
  const isBenefitHostingEnabled =
    benefitHostingStatus === null ? null : canAccessModule(benefitHostingStatus, user?.role);

  const router = useMemo(() => {
    const paths = (isGifting ? authenticatedGiftingPaths : authenticatedPaths) as AppPaths;

    return createBrowserRouter(
      createRoutesFromElements(
        <Route
          element={<AuthLayout />}
          errorElement={
            <Suspense fallback={null}>
              <ErrorPage />
            </Suspense>
          }
        >
          {/* AUTHENTICATED ROUTES */}
          <Route element={<AuthenticatedLayout />}>
            <Route
              path={paths.employeeDashboard.path}
              element={loginContextGuard(isEmployeeContext, <EmployeeDashboard />)}
              handle={{ crumb: () => paths.employeeDashboard.crumb }}
            />
            <Route handle={{ crumb: () => 'My Aslan' }}>
              <Route
                index
                element={getHomepageElement(
                  isEmployeeContext,
                  isGifting,
                  isRewardAndRecognitionEnabled,
                  isBenefitHostingEnabled
                )}
              />

              <Route
                element={
                  isEmployeeContext ? <Navigate to="/employee-dashboard" replace /> : <Outlet />
                }
              >
                <Route
                  path={paths.walletsAndPots.path}
                  handle={{ crumb: () => paths.walletsAndPots.crumb }}
                >
                  <Route index element={<WalletsAndPots />} />
                  <Route
                    path=":potId"
                    element={<PotPage />}
                    handle={{ crumb: () => paths.pot.crumb }}
                  />
                </Route>

                <Route
                  path={paths.reports.path}
                  element={<Reports />}
                  handle={{ crumb: () => paths.reports.crumb }}
                />
                <Route
                  path={paths.gifting.path}
                  element={<Gifting />}
                  handle={{ crumb: () => paths.gifting.crumb }}
                />

                {isGifting ? (
                  <Route
                    path={paths.manageEmployees.path}
                    handle={{ crumb: () => paths.manageEmployees.crumb }}
                  >
                    <Route index element={<GiftingManageEmployeesPage />} />
                    <Route
                      path={paths.employeeProfile.path}
                      element={<GiftingEmployeeProfile />}
                      loader={giftingEmployeeProfileLoader(queryClient)}
                      handle={{ crumb: getProfileCrumb }}
                    />
                    <Route
                      path={paths.importEmployees.path}
                      element={<GiftingImportEmployees />}
                      handle={{ crumb: () => paths.importEmployees.crumb }}
                    />
                  </Route>
                ) : (
                  <>
                    <Route
                      path={paths.importEmployees.path}
                      element={<DefaultImportEmployees />}
                      handle={{ crumb: () => paths.importEmployees.crumb }}
                    />
                    <Route
                      path={paths.employees.path}
                      handle={{ crumb: () => paths.employees.crumb }}
                    >
                      <Route index element={<DefaultManageEmployeesPage />} />
                      <Route
                        path={paths.employeeProfile.path}
                        element={<DefaultEmployeeProfile />}
                        loader={employeeProfileLoader(queryClient)}
                        handle={{ crumb: getProfileCrumb }}
                      />
                    </Route>
                  </>
                )}

                {isGifting ? (
                  <Route
                    path={paths.manageAdmins.path}
                    handle={{ crumb: () => paths.manageAdmins.crumb }}
                  >
                    <Route index element={<GiftingManageAdminsPage />} />
                    <Route
                      path={paths.adminProfile.path}
                      element={<AdminProfile />}
                      loader={adminProfileLoader(queryClient)}
                      handle={{ crumb: getProfileCrumb }}
                    />
                  </Route>
                ) : (
                  <Route path={paths.admins.path} handle={{ crumb: () => paths.admins.crumb }}>
                    <Route index element={<AdminsPage />} />
                    <Route
                      path={paths.adminProfile.path}
                      element={<AdminProfile />}
                      loader={adminProfileLoader(queryClient)}
                      handle={{ crumb: getProfileCrumb }}
                    />
                  </Route>
                )}
              </Route>
            </Route>

            {renderSharedAuthenticatedRoutes(
              paths,
              isRewardAndRecognitionEnabled,
              isBenefitHostingEnabled,
              isEmployeeContext,
              canApproveRecognitions
            )}
          </Route>

          {/* UNAUTHENTICATED ROUTES */}
          <Route element={<LoginLayout />}>
            <Route path={unauthenticatedPaths.authentication} element={<Authentication />} />
          </Route>

          <Route path="/auth/sso-callback" element={<SSOCallback />} />

          <Route element={<OnboardingLayout />}>
            <Route path={unauthenticatedPaths.onboardingGifting} element={<Onboarding />} />
          </Route>
        </Route>
      )
    );
  }, [
    queryClient,
    isGifting,
    isRewardAndRecognitionEnabled,
    isBenefitHostingEnabled,
    isEmployeeContext,
    canApproveRecognitions,
  ]);

  return (
    <Suspense fallback={null}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

export default Routes;
